<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Step } from '@client/components/steps';

  import { SignatureRouteForm } from '@client/pages/signatures/routing/modals/shared';

  import { GroupsService } from '@client/services/groups';
  import { RoutingService } from '@client/services/routing';
  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';

  import { appStore } from '@client/stores/app';

  import { RoutingList } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let loading = true;
  let currentInternalStep = 0;
  let maxStepReached = 0;
  let selectedRouteId = null;
  let routes = [];
  let route = null;
  let originalRoute = null;
  let senderGroups = [];
  let groupOptions = [];
  let tagOptions = [];
  let signaturesOptions = [];
  let isDisabled = false;

  const selectRoute = (routeId) => {
    selectedRouteId = routeId;
  };

  const handleInternalNext = (stepIndex) => {
    if (stepIndex === 0) {
      const selectedRoute = routes.find((r) => r._id === selectedRouteId);
      if (selectedRoute) {
        originalRoute = { ...selectedRoute };
        route = {
          ...selectedRoute,
          selectedGroups:
            selectedRoute.sendergroups?.map((g) => ({
              label: g.groupname,
              value: g.groupname
            })) || [],
          selectedTags: selectedRoute.selectedTags || []
        };
        currentInternalStep = 1;
        if (currentInternalStep > maxStepReached) {
          maxStepReached = currentInternalStep;
        }
      }
    }
  };

  const handleInternalBack = (stepIndex) => {
    if (stepIndex > 0) {
      currentInternalStep = stepIndex - 1;
    }
  };

  const onSignatureChange = async (e) => {
    const blockname = e.detail.value;
    const response = await TagBlocksService.getById(blockname);

    route = {
      ...route,
      blockname,
      tagblock: response
    };
  };

  const executeUpdate = async () => {
    $appStore.loading = true;

    try {
      const tags = (route.selectedTags || []).map(({ value }) => ({ text: value }));
      const selectedGroups = (route.selectedGroups || []).map(({ value }) => value);
      const sendergroups = senderGroups.filter(({ groupname }) => selectedGroups.includes(groupname));
      const { blockname, tagblock = {}, _id } = route;

      const payload = {
        _id,
        blockname,
        hide: route.hide,
        priority: route.priority - 1,
        header: route.header,
        sendergroups,
        status: route.status,
        tagblock,
        tags
      };

      await RoutingService.updateRoute(payload);

      notify($format('label.SUCCESS.UPDATE_ROUTE_FOR', { values: { blockname } }));

      step.data = {
        id: blockname,
        priority: route.priority,
        rollbackData: {
          originalRoute: originalRoute
        }
      };

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  const rollbackUpdate = async () => {
    $appStore.loading = true;

    try {
      const { originalRoute } = step.data.rollbackData;

      if (!originalRoute) {
        throw new Error($format('label.NO_ROLLBACK_DATA'));
      }

      const payload = {
        _id: originalRoute._id,
        blockname: originalRoute.blockname,
        hide: originalRoute.hide,
        priority: originalRoute.priority - 1,
        header: originalRoute.header,
        sendergroups: originalRoute.sendergroups || [],
        status: originalRoute.status,
        tagblock: originalRoute.tagblock || {},
        tags: originalRoute.tags || []
      };

      await RoutingService.updateRoute(payload);

      notify($format('label.SUCCESS.ROLLBACK_ROUTE'));

      return { success: true };
    } catch (error) {
      notify($format('label.FAILURE.ROLLBACK_ROUTE'), 'error');
      throw error;
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(async () => {
    loading = true;

    try {
      const [signaturesResponse, groupsResponse, routesResponse] = await Promise.all([
        SignaturesService.getAll(),
        GroupsService.getAll(),
        RoutingService.getAll()
      ]);

      const signatures = Array.isArray(signaturesResponse) ? signaturesResponse : [];
      senderGroups = Array.isArray(groupsResponse) ? groupsResponse : [];

      groupOptions = senderGroups.map((group) => ({
        label: group.groupname,
        value: group.groupname
      }));

      signaturesOptions = signatures.map(({ blockname }) => ({
        label: blockname,
        value: blockname
      }));

      // Get all unique tags
      const allTags = new Set();
      signatures.forEach((sig) => {
        (sig.tags || []).forEach((tag) => allTags.add(tag.text));
      });
      tagOptions = Array.from(allTags)
        .map((tag) => ({ label: tag, value: tag }))
        .sort((a, b) => a.label.localeCompare(b.label));

      const signatureRoutes = Array.isArray(routesResponse) ? routesResponse : [];

      routes = signatureRoutes.map((signature, index) => {
        const sendergroups = senderGroups.filter(
          ({ groupname }) => signature.sendergroups.filter((group) => groupname === group.groupname).length
        );

        const selectedTags = (signature.tags || []).map((tag) => ({
          label: tag.text,
          value: tag.text
        }));

        return {
          ...signature,
          priority: index + 1,
          sendergroups,
          selectedTags
        };
      });
    } catch (error) {
      console.error('Failed to load signature routing data:', error);
      notify($format('label.FAILURE.LOAD_DATA'), 'error');
    } finally {
      loading = false;
    }

    if (onExecute) {
      onExecute(executeUpdate);
    }

    if (onRollback) {
      onRollback(rollbackUpdate);
    }
  });

  $: {
    if (currentInternalStep === 0) {
      isDisabled = !selectedRouteId || loading;
    } else if (currentInternalStep === 1) {
      isDisabled = !route?.blockname || !route?.selectedGroups?.length || loading;
    }
  }

  $: nextDisabled = currentInternalStep !== 1 || isDisabled;

  $: selectedRouteSummary = selectedRouteId
    ? routes.find((r) => r._id === selectedRouteId)?.blockname || 'Route selected'
    : 'Not selected';
</script>

<div class="signature-routing-update-form">
  <Step
    active={currentInternalStep === 0}
    disabled={false}
    title={$format('label.SELECT_ROUTE')}
    summary={selectedRouteSummary}
    onNext={currentInternalStep === 0 ? () => handleInternalNext(0) : undefined}
    onStep={() => (currentInternalStep = 0)}
    nextDisabled={isDisabled}
  >
    <RoutingList {routes} bind:selectedRouteId onSelectRoute={selectRoute} showTags={true} {loading} />
  </Step>

  <Step
    active={currentInternalStep === 1}
    disabled={maxStepReached < 1}
    title={$format('label.EDIT_ROUTE')}
    summary={route?.blockname || $format('label.NOT_CONFIGURED')}
    onBack={currentInternalStep === 1 ? () => handleInternalBack(1) : undefined}
    onStep={() => {
      if (currentInternalStep >= 1) currentInternalStep = 1;
    }}
    nextDisabled={isDisabled}
  >
    {#if route}
      <SignatureRouteForm bind:route bind:groupOptions bind:signaturesOptions bind:tagOptions {onSignatureChange} />
    {/if}
  </Step>
</div>

<style lang="scss">
  .signature-routing-update-form {
    :global {
      .route {
        margin-bottom: var(--margin-base);
      }
    }
  }
</style>
